import axios from "../api";
import {
  D2DAcceptOrderPayload,
  D2DApiInterface,
  D2DBuyerOrdersResponse,
  D2DOrderActionResponse,
  D2DRejectOrderPayload,
  D2DRequestsFilters,
  D2DRequestsListResponse,
  D2DShipOrderPayload,
  MyLocatorInventoryResponse,
  OtherDealersInventoryResponse,
} from "./d2dApiInterfaces";
import { SaveLocatorEntryRequest, SaveLocatorEntryResponse } from "./types";

const appendFilterParam = (
  params: URLSearchParams,
  key: string,
  value?: string
) => {
  if (value && value.trim()) {
    params.append(key, value.trim());
  }
};

const d2dApi: D2DApiInterface = {
  saveLocatorEntry: (
    url: string,
    payload: SaveLocatorEntryRequest,
    token: string
  ): Promise<SaveLocatorEntryResponse> =>
    axios
      .postWithRetries(`${url}/v1/d2d/locator-entry`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res: any) => res.data),

  fetchOtherDealersInventory: (
    url: string,
    token: string,
    partNumber: string,
    accountNumber: string
  ): Promise<OtherDealersInventoryResponse> =>
    axios
      .getWithRetries(
        `${ensureBase(
          url
        )}/v1/d2d-inventory/inventory/${accountNumber}/${partNumber}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res: any) => {
        const status = res?.status;
        if (status && status >= 400) {
          throw normalizeInventoryError({
            response: { status, data: res?.data },
          });
        }
        return res.data;
      })
      .catch((err: any) => {
        throw normalizeInventoryError(err);
      }),

  fetchMyLocatorInventory: (
    url: string,
    token: string,
    accountNumber: string,
    pageSize?: number,
    pageToken?: string | null
  ): Promise<MyLocatorInventoryResponse> => {
    const params = new URLSearchParams();
    if (pageSize) params.append("pageSize", pageSize.toString());
    if (pageToken) params.append("pageToken", pageToken);

    const queryString = params.toString();
    const endpoint = `${ensureBase(
      url
    )}/v1/d2d-inventory/inventory/${accountNumber}${
      queryString ? `?${queryString}` : ""
    }`;

    return axios
      .getWithRetries(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res: any) => {
        const status = res?.status;
        if (status && status >= 400) {
          throw normalizeInventoryError({
            response: { status, data: res?.data },
          });
        }
        return res.data;
      })
      .catch((err: any) => {
        throw normalizeInventoryError(err);
      });
  },

  fetchD2DRequests: (
    url: string,
    token: string,
    accountId: string,
    page: number,
    pageSize: number,
    filters?: D2DRequestsFilters
  ): Promise<D2DRequestsListResponse> => {
    const base = ensureBase(url);
    const params = new URLSearchParams();
    params.append("sellerDealerId", accountId);
    params.append("page", page.toString());
    params.append("pageSize", pageSize.toString());
    appendFilterParam(params, "orderNumber", filters?.orderNumber);
    appendFilterParam(params, "dealerReference", filters?.dealerReference);
    appendFilterParam(params, "partNumber", filters?.partNumber);
    const serverStatuses = new Set<string>();
    const rejectReasonCodes = new Set<string>();

    filters?.statuses?.forEach((status) => {
      if (status === "D2D_CLAIMED") {
        rejectReasonCodes.add("AUTO");
        serverStatuses.add("REJECTED");
      } else if (status === "MANUAL_REJECTED") {
        rejectReasonCodes.add("MAN");
        serverStatuses.add("REJECTED");
      } else {
        serverStatuses.add(status);
      }
    });

    serverStatuses.forEach((status) =>
      appendFilterParam(params, "statuses", status)
    );
    rejectReasonCodes.forEach((code) =>
      appendFilterParam(params, "rejectReasonCds", code)
    );

    appendFilterParam(
      params,
      "dateRequestedFrom",
      filters?.dateRequestedFrom?.format("YYYY-MM-DD")
    );
    appendFilterParam(
      params,
      "dateRequestedTo",
      filters?.dateRequestedTo?.format("YYYY-MM-DD")
    );
    appendFilterParam(params, "lastChangedBy", filters?.lastChangedBy);
    const endpoint = `${base}/v1/d2d-order/orders?${params.toString()}`;

    return axios
      .getWithRetries(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res: any) => res.data);
  },

  acceptD2DOrder: (
    url: string,
    token: string,
    idToken: string,
    payload: D2DAcceptOrderPayload
  ): Promise<D2DOrderActionResponse> => {
    const base = ensureBase(url);
    const endpoint = `${base}/v1/d2d-order/order/action`;
    return axios
      .putWithRetries(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-id-token": idToken,
        },
      })
      .then((res: any) => res.data);
  },

  rejectD2DOrder: (
    url: string,
    token: string,
    idToken: string,
    payload: D2DRejectOrderPayload
  ): Promise<D2DOrderActionResponse> => {
    const base = ensureBase(url);
    const endpoint = `${base}/v1/d2d-order/order/action`;
    return axios
      .putWithRetries(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-id-token": idToken,
        },
      })
      .then((res: any) => res.data);
  },

  shipD2DOrder: (
    url: string,
    token: string,
    idToken: string,
    payload: D2DShipOrderPayload
  ): Promise<D2DOrderActionResponse> => {
    const base = ensureBase(url);
    const endpoint = `${base}/v1/d2d-order/order/action`;
    return axios
      .putWithRetries(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-id-token": idToken,
        },
      })
      .then((res: any) => res.data);
  },

  fetchD2DBuyerOrders: (
    url: string,
    token: string,
    buyerAccountId: string
  ): Promise<D2DBuyerOrdersResponse> => {
    const base = ensureBase(url);
    const endpoint = `${base}/v1/d2d-order/orders/buyer`;
    return axios
      .postWithRetries(
        endpoint,
        { buyerAccountId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res: any) => res.data);
  },
};

export default d2dApi;

export class D2DOtherDealerInventoryError extends Error {
  statusCode: number;

  error: string;

  details?: string;

  constructor(
    statusCode: number,
    message: string,
    error: string,
    details?: string
  ) {
    super(message);
    this.name = "D2DOtherDealerInventoryError";
    this.statusCode = statusCode;
    this.error = error;
    this.details = details;
  }
}

function normalizeInventoryError(err: any): D2DOtherDealerInventoryError {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 404) {
    return new D2DOtherDealerInventoryError(
      404,
      data?.message || "Resource not found",
      data?.error || "NOT_FOUND",
      data?.details || "The requested inventory was not found."
    );
  }
  if (status) {
    return new D2DOtherDealerInventoryError(
      status,
      data?.message ||
        `Failed to fetch other dealer inventory (status ${status})`,
      data?.error || "INVENTORY_FETCH_ERROR",
      data?.details
    );
  }
  return new D2DOtherDealerInventoryError(
    0,
    "Network error while fetching other dealer inventory",
    "NETWORK_ERROR",
    err?.message
  );
}

function ensureBase(provided: string): string {
  const base = provided?.trim();
  if (!base) {
    throw new Error(
      "D2D other dealer inventory base URL missing. Pass the base URL from ConfigContext.D2D_OTHER_DEALER_INVENTORY_API_URL"
    );
  }
  return base.replace(/\/$/, "");
}
